import { ITaxonomyTerms } from '@kontent-ai/delivery-sdk';
import { ProdClient } from 'client/client';
import { FeaturedContent } from 'components/modules/FeaturedContent';
import { HeadComponent } from 'components/pages/Head';
import OverviewTemplate from 'components/pages/Overview';
import { fetchInitialUrlFilter } from 'helpers/getLocaleKey';
import { ContentPage, FeaturedSpots, OverviewPage } from 'models';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { UpdatedTaxonomyTerms } from 'services/types';
import slugify from 'slugify';
import { withTranslations } from 'store/translations';
import { alphabeticalSort } from 'utils/alphabeticalSort';
import { onlyDefined } from 'utils/definedInArray';
import { fetchOverviewVariants } from 'utils/getOverviewPage';
import { OptionsProps } from 'utils/overviewFunctionality';

interface InsightsOverviewProps {
  contentPages?: ContentPage[];
  featuredSpot: FeaturedSpots;
  theme: ITaxonomyTerms[];
  type: ITaxonomyTerms[];
  urlTheme: string | null;
  overviewPage: OverviewPage;
}

export default function InsightsOverview({
  contentPages,
  featuredSpot,
  theme,
  type,
  urlTheme,
  overviewPage,
}: InsightsOverviewProps) {
  const { common, multiple, theme_topic, content_type } = withTranslations();
  const { locale } = useRouter();

  // ------------------------ FUNCTIONS ------------------------
  // -- Divide pages to show into arrays of 10 pages --
  const dividePagesToShow = () => {
    let dividePagesByTen = 0;
    // Use this instead of index due to some pages not showing w/ filters
    let currentIndex = 0;
    const pagesToShowTemp: [ContentPage[]] = [[]];
    contentPages?.forEach((page) => {
      if (currentIndex % 10 === 0 && currentIndex !== 0) {
        dividePagesByTen++;
      }
      const themeCodename = page.elements.tagsThemeTopic.value[0]?.codename;
      const contentTypeCodename = page.elements.tagsContentType.value[0]?.codename;
      if (
        (filterTheme.includes(themeCodename) || !filterTheme[0]) &&
        (filterType.includes(contentTypeCodename) || !filterType[0])
      ) {
        if (!Array.isArray(pagesToShowTemp[dividePagesByTen])) {
          pagesToShowTemp[dividePagesByTen] = [];
        }
        pagesToShowTemp[dividePagesByTen].push(page);
        currentIndex++;
      }
    });

    return onlyDefined(pagesToShowTemp);
  };

  // -- Functions that check amount of pages with filters --
  const updateFilterTheme = (): UpdatedTaxonomyTerms[] => {
    // setPagination(0);
    return theme
      .map((theme) => {
        let amountOfPagesWithTag = 0;
        contentPages?.forEach((page) => {
          const themeCodename = page.elements.tagsThemeTopic.value[0]?.codename;
          const typeCodename = page.elements.tagsContentType.value[0]?.codename;
          if (theme.codename === themeCodename && (filterType.includes(typeCodename) || !filterType[0])) {
            amountOfPagesWithTag++;
          }
        });
        return Object.assign({
          amountOfPagesWithTag: amountOfPagesWithTag,
          ...theme,
          name: theme_topic(theme.codename),
        });
      })
      .sort((a, b) => alphabeticalSort(a.name, b.name));
  };

  const updateFilterType = () => {
    // setPagination(0);
    return type
      .map((type) => {
        let amountOfPagesWithTag = 0;
        contentPages?.forEach((page) => {
          const themeCodename = page.elements.tagsThemeTopic.value[0]?.codename;
          const typeCodename = page.elements.tagsContentType.value[0]?.codename;
          if (type.codename === typeCodename && (filterTheme.includes(themeCodename) || !filterTheme[0])) {
            amountOfPagesWithTag++;
          }
        });
        return Object.assign({
          amountOfPagesWithTag: amountOfPagesWithTag,
          ...type,
          name: content_type(type.codename),
        });
      })
      .sort((a, b) => alphabeticalSort(a.name, b.name));
  };
  // -- Functions that check amount of pages with filters End --

  // -- URL based functions --
  const updateFiltersInUrl = () => {
    const removeQueries = router.asPath.includes('?') && router.asPath.split('?').reverse().pop();
    const urlArray =
      removeQueries !== false && removeQueries !== undefined ? removeQueries.split('/') : router.asPath.split('/');
    urlArray.length >= 3 && urlArray.pop();
    urlArray.push(...filterTheme.map((theme) => slugify(theme_topic(theme), { lower: true })));
    const urlString = filterType[0]
      ? urlArray.join('/') +
        `?${slugify(common('type'), { lower: true })}=${filterType.map((type) =>
          slugify(content_type(type), { lower: true }),
        )}`
      : urlArray.join('/');

    router.replace(urlString, undefined, { shallow: true, scroll: false });
  };

  const getTypeFilterFromUrl = () => {
    return type.filter((e) => {
      return (
        slugify(content_type(e.codename), { lower: true }) ===
        router.asPath.split(`${slugify(common('type'), { lower: true })}=`).slice(-1)[0]
      );
    }).length;
  };
  // -- URL based functions End --
  // ------------------------ FUNCTIONS END ------------------------

  // ------------------------ VARIABLES ------------------------
  const [filterTheme, setFilterTheme] = useState<string[]>(urlTheme ? [urlTheme] : []);
  const [filterType, setFilterType] = useState<string[]>([]);
  const [amountOfFilters, setAmountOfFilters] = useState(0);
  const [pagesToShow, setPagesToShow] = useState<ContentPage[][]>(dividePagesToShow());
  const [themeState, setThemeState] = useState<OptionsProps[]>([]);
  const [typeState, setTypeState] = useState<OptionsProps[]>([]);
  const router = useRouter();

  // ------------------------ VARIABLES END ------------------------

  // ------------------------ USE EFFECTS ------------------------
  useEffect(() => {
    const newThemes = updateFilterTheme();
    const newType = updateFilterType();
    setThemeState(newThemes);
    setTypeState(newType);

    setPagesToShow(dividePagesToShow());

    // If there's a theme in the URL set it to filterTheme
    urlTheme && fetchInitialUrlFilter('theme_topic', urlTheme, setFilterTheme, locale);
    const typeFromUrl =
      getTypeFilterFromUrl() > 0 && router.asPath.split(`${slugify(common('type'), { lower: true })}=`).slice(-1);
    typeFromUrl && fetchInitialUrlFilter('content_type', typeFromUrl, setFilterType, locale);
  }, []);

  // -- Runs when filters change
  useEffect(() => {
    setAmountOfFilters(filterType.length + filterTheme.length);
    updateFiltersInUrl();

    const newThemes = updateFilterTheme();
    const newType = updateFilterType();

    setThemeState(newThemes);
    setTypeState(newType);

    setPagesToShow(dividePagesToShow());
  }, [filterType, filterTheme]);
  // ------------------------ USE EFFECTS END ------------------------
  return (
    <>
      <HeadComponent page={overviewPage} />

      {!!featuredSpot && <FeaturedContent model={featuredSpot} />}
      <OverviewTemplate
        PagesToShow={pagesToShow}
        overviewTitle={multiple('insight')}
        amountOfFilters={amountOfFilters}
        filterTwo={{
          value: filterTheme,
          set: setFilterTheme,
          state: themeState,
          label: 'theme_topic',
        }}
        filterThree={{
          value: filterType,
          set: setFilterType,
          state: typeState,
          label: 'content_type',
        }}
      />
    </>
  );
}

export const getStaticInsightsProps = async ({ locale, urlParam }: { locale: string; urlParam?: string }) => {
  const languageCodename = locale ?? process.env.NEXT_PUBLIC_DEFAULT_LOCALE!;
  const contentPages = await ProdClient.items<ContentPage>()
    .type('content_page')
    .containsFilter('elements.category', ['insight'])

    .languageParameter(languageCodename)
    .equalsFilter('system.language', languageCodename)
    .orderByDescending('elements.summary_date')
    .elementsParameter([
      'url',
      'featured',
      'category',
      'summary__title',
      'summary__description',
      'summary__image_frontify',
      'part_of_campaign_page',
      'summary__date',
      'tags__sector',
      'tags__theme_topic',
      'tags__content_type',
    ])
    .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
    .toPromise()
    .then((response) => {
      return response.data.items
        .sort((a, b) =>
          Date.parse(a.elements.summaryDate.value ?? a.system.lastModified) <
          Date.parse(b.elements.summaryDate.value ?? b.system.lastModified)
            ? 1
            : -1,
        )
        .sort((a, b) =>
          (a.elements.featured.value[0]?.codename ? a.elements.featured.value[0].codename : 'no') >
          (b.elements.featured.value[0]?.codename ? b.elements.featured.value[0]?.codename : 'no')
            ? -1
            : 1,
        ) as ContentPage[];
    });

  const overviewPage = await ProdClient.items<OverviewPage>()
    .type('overview_page')
    .containsFilter('elements.category', ['insight'])
    .languageParameter(languageCodename)
    .limitParameter(1)
    .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
    .toPromise()
    .then((response) => {
      return response.data.items[0];
    });

  const featuredSpot =
    !!overviewPage &&
    !!overviewPage.elements.hero.value[0] &&
    (await ProdClient.item<FeaturedSpots>(overviewPage.elements.hero.value[0])
      .languageParameter(languageCodename)
      .depthParameter(2)
      .toPromise()
      .then((response) => {
        return response.data.item;
      }));

  const theme = await ProdClient.taxonomy('theme_topic')
    .toPromise()
    .then((response) => {
      return response.data.taxonomy.terms;
    });

  const type = await ProdClient.taxonomy('media_type')
    .toPromise()
    .then((response) => {
      return response.data.taxonomy.terms;
    });

  const variants = await fetchOverviewVariants('insight');

  return {
    variants,
    contentPages,
    featuredSpot,
    theme,
    type,
    urlTheme: urlParam ?? null,
    overviewPage,
  };
};
